// RGM Studio · Final mix. Renders the song offline with the SAME scheduling
// code used for live playback (StudioEngine._startAt) so the export matches
// what the artist hears: clip edits, fades, gain, beat volume, track volume /
// pan / mute / solo and the mastering preset. Then encodes MP3 256 kbps.
import { StudioEngine, requestWakeLock } from "./studioEngine";
import { ensureGate, fxTailMs } from "./studioFx";

export const MIX_RATE = 44100;
export const MP3_KBPS = 256;
export const MAX_MIX_MS = 10 * 60 * 1000;
const LEAD_SEC = 0.3; // lets master/bus parameter ramps settle, trimmed after
const TAIL_MS = 400;

const toDb = (v) => (v > 0 ? 20 * Math.log10(v) : -120);

export function audibleTakes(tracks, takes) {
  const anySolo = tracks.some((t) => t.solo);
  const ok = new Set(tracks.filter((t) => !t.mute && (!anySolo || t.solo)).map((t) => t.track_id));
  return takes.filter((k) => ok.has(k.track_id));
}

export function mixLengthMs({ beatDurationMs, beatClip, takes, fxTailMs = 0 }) {
  const be = Math.min(beatDurationMs, beatClip?.clip_end_ms || beatDurationMs);
  let end = be;
  for (const k of takes) {
    const win = (k.clip_end_ms || k.duration_ms || 0) - (k.clip_start_ms || 0);
    if (win > 0) end = Math.max(end, (k.beat_offset_ms || 0) + win + fxTailMs);
  }
  return Math.round(end + TAIL_MS);
}

export function analyse(channels, from = 0) {
  let peak = 0;
  let sum = 0;
  let n = 0;
  for (const ch of channels) {
    for (let i = from; i < ch.length; i++) {
      const v = ch[i] < 0 ? -ch[i] : ch[i];
      if (v > peak) peak = v;
      sum += ch[i] * ch[i];
      n++;
    }
  }
  return { peakDb: toDb(peak), rmsDb: toDb(Math.sqrt(sum / Math.max(1, n))) };
}

export async function renderMixdown({ engine, project, tracks, takes, onStage, fxAllowed = false }) {
  const stage = (s) => onStage?.(s);
  const Offline = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  if (!Offline) throw new Error("This browser can't build a final mix — update iOS / your browser");
  const beatRef = project.beat_ref || {};
  const beatClip = {
    clip_start_ms: beatRef.clip_start_ms || 0, clip_end_ms: beatRef.clip_end_ms || 0,
    clip_gain_db: beatRef.clip_gain_db || 0, fade_in_ms: beatRef.fade_in_ms || 0, fade_out_ms: beatRef.fade_out_ms || 0,
  };
  const playing = audibleTakes(tracks, takes);
  stage("Loading beat and vocals…");
  const beatBuffer = await engine.loadBuffer(engine.beatUrl);
  if (!beatBuffer) throw new Error("The beat couldn't be read — re-upload it and try again");
  const bufs = await Promise.all(playing.map((k) => engine.loadBuffer(k.clip_url)));
  const bad = bufs.findIndex((b) => !b);
  if (bad >= 0) throw new Error(`A vocal clip couldn't be read (${tracks.find((t) => t.track_id === playing[bad].track_id)?.name || "vocal"}) — nothing was exported`);

  const fxTail = fxAllowed ? Math.max(0, ...tracks.map((t) => fxTailMs(t.fx))) : 0;
  const lengthMs = mixLengthMs({ beatDurationMs: beatBuffer.duration * 1000, beatClip, takes: playing, fxTailMs: fxTail });
  if (lengthMs > MAX_MIX_MS) throw new Error("Songs longer than 10 minutes can't be exported on mobile yet");
  const frames = Math.ceil((lengthMs / 1000 + LEAD_SEC) * MIX_RATE);
  const ctx = new Offline(2, frames, MIX_RATE);

  // Same graph + scheduler as live playback, on the offline context.
  const r = Object.create(StudioEngine.prototype);
  Object.assign(r, {
    audioCtx: ctx, _offline: true, _buffers: engine._buffers, _voices: [], _buses: new Map(),
    _masterPreset: project.master?.preset || "off", _masterIntensity: project.master?.intensity ?? 50,
    _beatVol: typeof project.beat_volume === "number" ? project.beat_volume : 1,
    _masterVol: typeof project.master_volume === "number" ? project.master_volume : 1,
    _mixTracks: tracks, beatBuffer, beatEl: null, _fxAllowed: !!fxAllowed,
  });
  await ensureGate(ctx);
  r._buildGraph();
  await r._startAt(0, { takes: playing, beatClip }, LEAD_SEC);

  stage("Mixing your song…");
  const wake = await requestWakeLock();
  let out;
  try { out = await ctx.startRendering(); }
  finally { try { wake?.release?.(); } catch { /* */ } }
  const skip = Math.round(LEAD_SEC * MIX_RATE);
  const L = out.getChannelData(0).subarray(skip);
  const R = out.getChannelData(1).subarray(skip);
  const stats = analyse([L, R]);
  if (stats.rmsDb < -60 || stats.peakDb < -50) throw new Error("The mix is silent — check beat volume, mutes and solos");
  return { left: L, right: R, durationMs: Math.round((L.length / MIX_RATE) * 1000), expectedMs: lengthMs, ...stats, vocalClips: playing.length };
}

const clamp16 = (v) => (v <= -1 ? -32768 : v >= 1 ? 32767 : Math.round(v * (v < 0 ? 32768 : 32767)));

export async function encodeMp3({ left, right, onProgress }) {
  const { Mp3Encoder } = await import("@breezystack/lamejs");
  const enc = new Mp3Encoder(2, MIX_RATE, MP3_KBPS);
  const block = 1152 * 40;
  const parts = [];
  const l16 = new Int16Array(block);
  const r16 = new Int16Array(block);
  for (let i = 0; i < left.length; i += block) {
    const n = Math.min(block, left.length - i);
    for (let j = 0; j < n; j++) { l16[j] = clamp16(left[i + j]); r16[j] = clamp16(right[i + j]); }
    const mp3 = enc.encodeBuffer(l16.subarray(0, n), r16.subarray(0, n));
    if (mp3.length) parts.push(new Uint8Array(mp3));
    if ((i / block) % 8 === 0) {
      onProgress?.(Math.min(99, Math.round((i / left.length) * 100)));
      await new Promise((res) => setTimeout(res, 0)); // keep the UI responsive
    }
  }
  const end = enc.flush();
  if (end.length) parts.push(new Uint8Array(end));
  onProgress?.(100);
  const blob = new Blob(parts, { type: "audio/mpeg" });
  if (blob.size < 16 * 1024) throw new Error("Export failed — the file came out empty");
  return blob;
}
